import { ActionTypes, InputTypesEnum } from "../tsmodels/SharedModel";
import moment from "moment";
import { DATE_FORMAT, DOLLOR_SIGN } from "./Constants";

// convert the given number such that it can be display as decimals and corresponding size parameter
export const sizeConversion = (bytes: number, decimal = 2) => {
  const k = 1024;
  const dm = decimal < 0 ? 0 : decimal;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  if (bytes < 0) return "";
  else if (bytes === 0) return "0 Bytes";
  else {
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }
};

export const setInputValue = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  } else if (e?.value && e?.label) {
    return e;
  } else {
    return e?.target?.value;
  }
};

// remove - from the enum values
export const textFormatv1 = (str: string) => str?.split("_")?.join(" ");

export const dateCheck = (
  inputValue: string,
  start: string | null,
  end: string | null
) => {
  if (inputValue) {
    if (start && end) {
      return moment(inputValue).isBetween(start, end, undefined, "[]");
    } else if (start && (end === undefined || end === null)) {
      return moment(inputValue).isAfter(start);
    } else if (end && (start === undefined || start === null)) {
      return moment(inputValue).isBefore(end);
    } else return true;
  } else return false;
};

export const compaireObjects = (data: any, fields: string[]) => {
  const keys = Object.keys(data);
  const modifiedFields = fields?.filter((each) => each);
  const newData = {};
  if (modifiedFields?.length > 0) {
    keys.forEach((key) => {
      if (modifiedFields?.includes(key)) {
        Object.assign(newData, { [key]: data?.[key] });
      }
    });
  }
  return newData;
};

export const performSum = (values: number[]) => {
  return values?.reduce((acc: any, curr: any) => acc + curr, 0)?.toFixed(2);
};

export const performMin = (values: number[]) => {
  return Math.min(...values)?.toFixed(2);
};

export const performMax = (values: number[]) => {
  return Math.max(...values)?.toFixed(2);
};

export const performAvg = (values: number[]) => {
  const sum = performSum(values);
  return values?.length > 0 ? (sum / values?.length)?.toFixed(2) : 0;
};

export const performCount = (data: any, field: string, size = true) => {
  const values = data?.map((each: any) => each?.[field] || 0);
  const newValues = new Set(values);
  return size ? newValues?.size : newValues;
};

export const performActions = (
  data: any,
  field: string,
  action: ActionTypes
) => {
  // console.log(data, field, action)
  if (data && data?.length > 0) {
    const values = data
      ?.map((each: any) => each?.[field] || 0)
      ?.filter((filEach: any) => typeof filEach === "number");

    switch (action) {
      case ActionTypes.SUM:
        return performSum(values);
      case ActionTypes.MIN:
        return performMin(values);
      case ActionTypes.MAX:
        return performMax(values);
      case ActionTypes.AVG:
        return performAvg(values);
      case ActionTypes.COUNT:
        return performCount(data, field);
      default:
        return data;
    }
  } else return [];
};

export const getMinMaxValues = (data: any, field: string) => {
  const filteredData =
    data && data?.length > 0 ? data?.map((each: any) => each?.[field]) : [];
  if (filteredData?.length > 0) {
    return { min: +performMin(filteredData), max: +performMax(filteredData) };
  } else return { min: 0, max: 0 };
};

export const textFormatWithSymbol = (
  x: string,
  symbol: string = ",",
  decimals: number = 2,
  groupSize: number = 3
) => {
  const formattedNumber = parseFloat(x)?.toFixed(decimals);
  const regex = new RegExp(`\\B(?=(\\d{${groupSize}})+(?!\\d))`, "g");
  return formattedNumber.replace(regex, symbol);
};

export const dateFormatTo = (param: string, dateFormat = DATE_FORMAT) =>
  moment(param).format(dateFormat);

export const symbolFunc = (item: string, symbol = DOLLOR_SIGN) => {
  return parseFloat(item) > 0
    ? `${symbol}${item}`
    : `-${symbol}${Math.abs(parseFloat(item))}`;
  // return parseFloat(item) > 0 ? `${symbol}${item}` : `(${symbol}${Math.abs(parseFloat(item))})`;
};

export const valueFormat = (value: string, symbol: string) => {
  return symbolFunc(textFormatWithSymbol(value, symbol));
};

export const convertTimeToEST = (value: string) => {
  const utcTime = moment.utc(value, 'HH:mm:ss');

  // Convert to Eastern Standard Time (EST)
  const estTime = utcTime.tz('America/New_York');

  if (estTime?.isValid()) {
  // Format the EST time as desired
  const formattedEST = estTime.format("HH:mm A");

  return `${formattedEST} EST`
  } else {
    return ''
  }
  
}

// get modified value of the event based on the input type
export const getTypeValue = (
  event: any,
  type: InputTypesEnum,
  saving = false
) => {
  if (event && type) {
    switch (type) {
      case InputTypesEnum.TEXT:
        return event?.target ? event?.target?.value : event;

      case InputTypesEnum.DROPDOWN:
        return saving ? event : event?.value;

      case InputTypesEnum.SWITCH:
        return event?.target ? event?.target?.checked : event;

      case InputTypesEnum.MULTIDROPDOWN:
      default:
        return event;
    }
  } else return event;
};

// modify object - get list of new keys and remove the object properties that are not there in new keys
export const getNewObject = (obj: any, newKeys: string[]) => {
  if (obj && newKeys && newKeys?.length > 0) {
    const newObj = {}
    const keys = Object.keys(obj);
    const modifiedKeys = keys?.filter(key => newKeys?.includes(key))
    modifiedKeys?.forEach(key => {
      Object.assign(newObj, { [key]: obj?.[key] })
    })
    return newObj
  } else return {}
}

export const getStringValue = (str?: string | null, symbol: string = ".") =>
  str?.split(symbol)?.pop() || "";

export const getPercentage = (top: number, bottom: number) => {
  if (isNaN(top) || isNaN(bottom)) {
    return 0
  } else if (bottom === 0) {
    return 0
  } else {
    const result = ((top / bottom) * 100)
    return parseFloat(result?.toFixed(2));
  }
}

